import React, { useState } from "react";
import { SelectProduct } from "../../../types/salon.types";
import { Minus, Plus } from "lucide-react";
import { useUser } from "src/shared/context/UserContext";
import { borderHighlightOptions } from "src/shared/modules/profile/theme/themeConstants";
import { useUserTheme } from "src/shared/hooks/useUserTheme";

interface OrderItemProps {
  product: SelectProduct;
  onQuantityChange: (productId: string, newQuantity: number) => void;
  onPriceChange: (productId: string, newPrice: string) => void;
  onRemove: (productId: string) => void;
}

const OrderItem: React.FC<OrderItemProps> = ({ product, onQuantityChange, onPriceChange, onRemove }) => {
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [localPrice, setLocalPrice] = useState(product.amount);
  const { user } = useUser();

  const themeKey = user?.themes.backgroundColour || "DodgerBlue";
  const highlightClass = borderHighlightOptions[themeKey];
  const { bgClass, fontClass } = useUserTheme();

  const quantity = product.quantity || 1;

  const handleDecrease = () => {
    if (quantity > 1) {
      onQuantityChange(product._id.$oid, quantity - 1);
    }
  };

  const handleIncrease = () => {
    onQuantityChange(product._id.$oid, quantity + 1);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setLocalPrice(value);
      onPriceChange(product._id.$oid, value);
    }
  };

  const handlePriceBlur = () => {
    setIsEditingPrice(false);
    if (localPrice === "" || parseFloat(localPrice) <= 0) {
      setLocalPrice(product.amount);
      onPriceChange(product._id.$oid, product.amount);
    }
  };

  const totalPrice = (parseFloat(localPrice) || 0) * quantity;

  return (
    <div className={`relative p-2 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-200 
    ${fontClass} ${highlightClass ? `${highlightClass} ring-1 ring-opacity-40` : "border-transparent"}`}>
      {/* X Button - Top Right Corner */}
      <button
        onClick={() => onRemove(product._id.$oid)}
        className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200 shadow-md hover:scale-110"
        aria-label="Remove item"
      >
        <span className="text-sm font-bold">×</span>
      </button>

      {/* Product Name - Top Left */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-8">
          <span className="text-sm font-medium text-gray-800 truncate block">{product.productName}</span>
        </div>
      </div>

      {/* Bottom Row: Quantity Controls, Quantity Badge, Price */}
      <div className="flex items-center justify-between mt-2">
        {/* Quantity Controls - Left */}
        <div className="flex items-center overflow-hidden">
          <button onClick={handleDecrease} disabled={quantity <= 1} className={`rounded-full p-1 hover:opacity-90 transition-all ${bgClass}`}>
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="px-3 py-1 text-sm font-semibold text-gray-800 min-w-[28px] text-center">{quantity}</span>
          <button onClick={handleIncrease} className={`rounded-full p-1 hover:opacity-90 transition-all ${bgClass}`}>
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Quantity Badge & Price - Right */}
        <div className="flex items-center gap-3">
          {/* <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">×{quantity}</span> */}

          {isEditingPrice ? (
            <input
              type="text"
              value={localPrice}
              onChange={handlePriceChange}
              onBlur={handlePriceBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handlePriceBlur();
                }
                if (e.key === "Escape") {
                  setIsEditingPrice(false);
                  setLocalPrice(product.amount);
                }
              }}
              className="w-20 px-2 py-1 text-sm text-right border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
              autoFocus
            />
          ) : (
            <span
              className="text-sm font-semibold text-gray-900 cursor-pointer hover:text-blue-600 hover:underline min-w-[80px] text-right"
              onClick={() => {
                setIsEditingPrice(true);
                setLocalPrice(product.amount);
              }}
              title="Click to edit price"
            >
              {totalPrice.toFixed(2)} QAR
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderItem;
